import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';

import {
  CustomFieldDefinitionModalComponent,
  CustomFieldDefinitionModalData,
} from '../../../../../../../common/components/custom-field-definition-modal/custom-field-definition-modal.component';
import { MODAL_WIDTH_SMALL } from '../../../../../../../common/models/constants';
import { CUSTOM_FIELD_DATA_TYPE, CUSTOM_FIELD_TARGET, CustomFieldDefinition } from '../../../../../../../common/models/main';
import { ApiService } from '../../../../../../../common/services/api.service';
import { CustomFieldDefinitionsService } from '../../../../../../../common/services/custom-field-definitions.service';

/** Gestion del catalogo de custom fields de la organizacion: una pestana por target, con orden arrastrable */
@Component({
  selector: 'app-organization-custom-fields',
  templateUrl: './organization-custom-fields.component.html',
  styleUrls: ['./organization-custom-fields.component.scss'],
})
export class OrganizationCustomFieldsComponent implements OnInit {

  TARGETS = Object.values(CUSTOM_FIELD_TARGET);
  TARGET = CUSTOM_FIELD_TARGET;
  DATA_TYPE = CUSTOM_FIELD_DATA_TYPE;

  selectedTarget: CUSTOM_FIELD_TARGET = CUSTOM_FIELD_TARGET.Record;
  active: CustomFieldDefinition[] = [];
  archived: CustomFieldDefinition[] = [];
  loading = false;

  constructor(
    private api: ApiService,
    private definitionsService: CustomFieldDefinitionsService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  selectTarget(target: CUSTOM_FIELD_TARGET): void {
    this.selectedTarget = target;
    this.load();
  }

  load(): void {
    this.loading = true;
    const target = this.selectedTarget;
    this.definitionsService.list({ target }).subscribe({
      next: definitions => {
        // Al cambiar rapido de pestana puede llegar tarde la respuesta de otro target
        if (target !== this.selectedTarget) return;
        this.loading = false;
        this.active = definitions.filter(definition => !definition.archived);
        this.archived = definitions.filter(definition => definition.archived);
      },
      error: () => this.loading = false,
    });
  }

  openNew(): void {
    this.openModal({ initial: { target: this.selectedTarget, order: this.nextOrder() }, lockTarget: true });
  }

  edit(definition: CustomFieldDefinition): void {
    this.openModal({ definition });
  }

  private openModal(data: CustomFieldDefinitionModalData): void {
    this.dialog.open(CustomFieldDefinitionModalComponent, { ...MODAL_WIDTH_SMALL, data }).afterClosed().subscribe(result => {
      if (result) this.load();
    });
  }

  archive(definition: CustomFieldDefinition): void {
    this.setArchived(definition, true);
  }

  /** Al final de la lista: su orden antiguo puede coincidir con el de las activas */
  unarchive(definition: CustomFieldDefinition): void {
    this.setArchived(definition, false, this.nextOrder());
  }

  private nextOrder(): number {
    return this.active.reduce((max, definition) => Math.max(max, definition.order), -1) + 1;
  }

  private setArchived(definition: CustomFieldDefinition, archived: boolean, order?: number): void {
    this.definitionsService.update(definition.id, order === undefined ? { archived } : { archived, order }).subscribe(() => {
      this.api.log('customFieldDefinitions.saved');
      this.load();
    });
  }

  drop(event: CdkDragDrop<CustomFieldDefinition[]>): void {
    moveItemInArray(this.active, event.previousIndex, event.currentIndex);

    const requests: Array<Observable<CustomFieldDefinition>> = [];
    this.active.forEach((definition, index) => {
      if (definition.order === index) return;
      definition.order = index;
      requests.push(this.definitionsService.update(definition.id, { order: index }));
    });
    if (!requests.length) return;

    forkJoin(requests).subscribe({
      next: () => this.api.log('customFieldDefinitions.saved'),
      // Tras un fallo parcial el orden local ya no refleja el guardado
      error: () => this.load(),
    });
  }

  /** Subtipos de actor a los que aplica la definicion, ya traducidos y unidos por coma. Null aplica a todos */
  actorSubtypesLabel(definition: CustomFieldDefinition): string | null {
    if (definition.target !== CUSTOM_FIELD_TARGET.Actor || !definition.actorSubtypes?.length) return null;
    const labels: string[] = definition.actorSubtypes.map(subtype => this.translate.instant(`objects.${subtype}`));
    return labels.join(', ');
  }

  /** Etiquetas de las opciones activas de un campo `select`, unidas por coma */
  optionsLabel(definition: CustomFieldDefinition): string {
    return (definition.options ?? []).filter(option => !option.archived).map(option => option.label).join(', ');
  }
}
